/**
 * ตัวช่วยอ่าน error "ของในตะกร้าสั่งไม่ได้แล้ว" (code = ERROR_ITEM_UNAVAILABLE)
 *
 * เคสจริง: ลูกค้า/แคชเชียร์กดใส่ตะกร้าไปแล้ว ระหว่างนั้นพนักงานปิดขาย/กดของหมด
 * → พอกดส่งเข้าครัวจะโดน 422 กลับมา BE ส่ง `unavailable_items` มาให้ครบทุกแถวที่พัง
 *   (ดู api: OrderAvailabilityHelper + ItemUnavailableException)
 *
 * ใช้ร่วมกันทั้ง POS CartPanel และ Service (QR ลูกค้า) เพื่อให้ข้อความ + พฤติกรรมตรงกัน
 *
 * `item_index` = ลำดับใน payload.items ที่เพิ่งส่งไป — ทั้งสองฝั่ง map ตะกร้า 1:1 ตามลำดับ
 * จึงใช้อ้างแถวในตะกร้าได้ตรงตัว
 */

// reason จาก BE → i18n key (namespace _cart) — เพิ่ม reason ใหม่ที่นี่ที่เดียว
const REASON_KEYS = {
	inactive: '_cart.This item is closed for sale',
	parent_closed: '_cart.The main product is closed for sale',
	not_sold_here: '_cart.This item is no longer sold at this branch',
	out_of_stock: '_cart.Sold out',
	ingredient_out: '_cart.Ingredient ran out',
	dine_in_only: '_cart.Dine-in only',
	option_not_sold_here: '_cart.This option is no longer available',
	option_out_of_stock: '_cart.Option sold out',
	option_ingredient_out: '_cart.Ingredient ran out',
};

const FALLBACK_KEY = '_cart.Unavailable';

/**
 * reason ที่แปลว่า "ของเคยขายได้ แต่หมด" — ใช้แยก 2 กลุ่มข้อความฝั่งลูกค้า
 * ที่เหลือทั้งหมด (inactive, parent_closed, not_sold_here, dine_in_only,
 * option_not_sold_here) = ตั้งค่าไว้ไม่ให้ขาย ไม่ใช่ของหมด
 */
const STOCK_REASONS = new Set([
	'out_of_stock',
	'ingredient_out',
	'option_out_of_stock',
	'option_ingredient_out',
]);

export function isItemUnavailableError(err) {
	return err?.response?.data?.code === 'ERROR_ITEM_UNAVAILABLE';
}

/**
 * แปลง error เป็นรายการที่ normalize แล้ว (คืน [] ถ้าไม่ใช่ error ชนิดนี้)
 * @returns {Array<{itemIndex:number, productId:number, productName:string,
 *                  optionName:string, inventoryName:string, reason:string}>}
 */
export function parseUnavailableItems(err) {
	if (!isItemUnavailableError(err)) return [];

	const rows = err.response.data.unavailable_items;
	if (!Array.isArray(rows)) return [];

	return rows.map(r => ({
		itemIndex: Number(r.item_index),
		productId: Number(r.product_id) || 0,
		productName: r.product_name || '',
		optionName: r.option_name || '',
		inventoryName: r.inventory_name || '',
		reason: r.reason || '',
	}));
}

/**
 * รวมเป็น map: itemIndex → เหตุผลของแถวนั้น (แถวเดียวอาจพังหลาย option → เก็บทุกตัว)
 * @returns {Map<number, Array>}
 */
export function groupByItemIndex(items) {
	const map = new Map();
	items.forEach(item => {
		if (!Number.isInteger(item.itemIndex)) return;
		if (!map.has(item.itemIndex)) map.set(item.itemIndex, []);
		map.get(item.itemIndex).push(item);
	});
	return map;
}

/**
 * ป้ายละเอียดที่ติดบนแถวในตะกร้า เช่น "ไข่มุก - ตัวเลือกนี้ของหมด" / "วัตถุดิบหมด (กุ้งสด)"
 *
 * **ใช้เฉพาะฝั่ง POS (พนักงาน)** — เพราะพนักงานต้องรู้ว่าต้องไปเติม/เปิดขายอะไร
 * ฝั่งลูกค้า (QR) จงใจไม่ใช้: โชว์แค่ป้าย "ของหมด" รวม ๆ ต่อแถว เพราะสาเหตุ/ชื่อวัตถุดิบ
 * เป็นข้อมูลหลังบ้าน (สต๊อก/คอนฟิกสาขา) ที่ลูกค้าไม่ได้ประโยชน์
 *
 * `t` = ฟังก์ชัน $t ของ component (util ไม่ผูกกับ i18n instance เอง)
 */
export function reasonLabel(item, t) {
	const base = t(REASON_KEYS[item.reason] || FALLBACK_KEY);

	// option พัง → บอกว่า option ไหน ลูกค้าจะได้แก้เฉพาะตัวเลือกไม่ต้องทิ้งทั้งจาน
	const withOption = item.optionName ? `${t(item.optionName)} - ${base}` : base;

	// วัตถุดิบหมด → บอกชื่อวัตถุดิบ (พนักงานรู้ทันทีว่าต้องไปเติมอะไร)
	return item.inventoryName ? `${withOption} (${t(item.inventoryName)})` : withOption;
}

/**
 * ป้ายฝั่งลูกค้า (QR) — ยุบทุก reason เหลือ **2 กลุ่ม** ด้วยภาษาบริการ
 *
 *   ของหมดจริง        → "สินค้าหมด"
 *   ตั้งค่าไม่ให้ขาย   → "สินค้าไม่พร้อมขาย"
 *
 * ลูกค้าไม่ต้องรู้ว่าหมดเพราะวัตถุดิบตัวไหนหรือสาขาปิดขาย — รู้แค่ว่า "หมด" กับ
 * "ยังไม่เปิดขาย" ก็ตัดสินใจได้แล้ว และคำว่า "ของหมด" เฉย ๆ ห้วนเกินไปสำหรับหน้าลูกค้า
 * (ฝั่ง POS ใช้ reasonLabel() ที่ละเอียดกว่า เพราะพนักงานต้องรู้ว่าต้องไปแก้อะไร)
 *
 * @param  {Array}     issues  ธง `_unavailable` ของแถวนั้น
 * @param  {Function}  t       ฟังก์ชัน $t ของ component
 */
export function customerLabel(issues, t) {
	const list = Array.isArray(issues) ? issues : [];

	// แถวเดียวติดหลายเหตุ (หลาย option) → "หมด" ชนะ เพราะชัดเจนกว่าสำหรับลูกค้า
	return list.some(i => STOCK_REASONS.has(i.reason))
		? t('_cart.Out of stock')
		: t('_cart.Not available for sale');
}

/**
 * ข้อความสรุปสำหรับ toast — รวมชื่อเมนูที่พัง (ตัดซ้ำ) ไม่ให้ยาวเกินอ่าน
 */
export function summaryText(items, t) {
	const names = [...new Set(items.map(i => i.productName).filter(Boolean))];
	if (names.length === 0) return t('_cart.Some items are no longer available');

	const shown = names.slice(0, 3).map(n => t(n)).join(', ');
	const more = names.length > 3 ? ` +${names.length - 3}` : '';

	return `${t('_cart.No longer available')}: ${shown}${more}`;
}
